/**
 * Реестр поставщиков (модуль 2.3).
 *
 * Поставщики появлялись в реестре только из КП — по имени, без ИНН и
 * контактов. Здесь снабженец загружает свой реестр файлом (Excel, PDF):
 * наименование, ИНН, КПП, контактное лицо, телефон, почта, специализация.
 * Поставщик, уже известный по КП, дополняется — он ищется по ИНН, затем по
 * наименованию без кавычек и регистра; новые заводятся. Пустая ячейка файла
 * прежнего не стирает. Строку можно поправить руками.
 */
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useImportMtoSuppliersMutation,
  useListMtoSuppliersQuery,
  useUpdateMtoSupplierMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import type { MtoRegistryImport, MtoSupplierRegistryRow } from '@entities/mto';
import { apiErrorMessage, fmtDate } from '@entities/mto';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input } from '@shared/ui/Input';
import { IconSearch } from '@shared/ui/icons';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 15px;
  font-weight: 700;
`;

const Hint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Small = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.4;
`;

const Result = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const SearchWrap = styled.label`
  position: relative;
  flex: 1 1 260px;
  max-width: 420px;

  svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(2.5)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.muted};
  }

  input {
    padding-left: ${({ theme }) => theme.spacing(8)};
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.surface};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const Name = styled.div`
  font-weight: 600;
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

const EditActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const DetailCell = styled.td`
  background: ${({ theme }) => theme.colors.surface};
`;

/** Реквизиты поставщика одной строкой: КПП, специализация, адрес. */
function detailsLine(s: MtoSupplierRegistryRow): string {
  const d = s.raw_data ?? {};
  return [d.kpp ? `КПП ${d.kpp}` : null, d.address, d.site].filter(Boolean).join(' · ');
}

function matches(s: MtoSupplierRegistryRow, q: string): boolean {
  if (!q) return true;
  const d = s.raw_data ?? {};
  const hay =
    `${s.name} ${s.inn ?? ''} ${s.contact ?? ''} ${d.specialization ?? ''} ${d.address ?? ''}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

function EditRow({
  supplier,
  onDone,
  onError,
}: {
  supplier: MtoSupplierRegistryRow;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState(supplier.name);
  const [inn, setInn] = useState(supplier.inn ?? supplier.raw_data?.inn_raw ?? '');
  const [contact, setContact] = useState(supplier.contact ?? '');
  const [specialization, setSpecialization] = useState(supplier.raw_data?.specialization ?? '');
  const [update, { isLoading }] = useUpdateMtoSupplierMutation();

  async function save() {
    onError('');
    try {
      await update({
        id: supplier.id,
        name: name.trim(),
        inn: inn.replace(/\s/g, '') || null,
        contact: contact.trim() || null,
        specialization: specialization.trim() || null,
      }).unwrap();
      onDone();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось сохранить поставщика.');
    }
  }

  return (
    <>
      <EditGrid>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Наименование"
          aria-label="Наименование поставщика"
        />
        <Input
          value={inn}
          inputMode="numeric"
          onChange={(e) => setInn(e.target.value)}
          placeholder="ИНН: 10 или 12 цифр"
          aria-label="ИНН поставщика"
        />
        <Input
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="Контакты: лицо, телефон, почта"
          aria-label="Контакты поставщика"
        />
        <Input
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          placeholder="Что поставляет"
          aria-label="Специализация поставщика"
        />
      </EditGrid>
      <EditActions>
        <Button
          type="button"
          $variant="primary"
          $size="sm"
          onClick={save}
          disabled={!name.trim() || isLoading}
        >
          {isLoading ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        <Button type="button" $variant="ghost" $size="sm" onClick={onDone}>
          Отмена
        </Button>
      </EditActions>
    </>
  );
}

export function MtoSuppliers() {
  const { can_write } = useAgentPermission('mto');
  const { data, isLoading, isError } = useListMtoSuppliersQuery();
  const [importFile, { isLoading: importing }] = useImportMtoSuppliersMutation();
  const [report, setReport] = useState<MtoRegistryImport | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<string | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const rows = useMemo(() => items.filter((s) => matches(s, search.trim())), [items, search]);
  const withInn = items.filter((s) => s.inn).length;

  async function handleFile(file: File) {
    setError('');
    setReport(null);
    const form = new FormData();
    form.append('file', file, file.name);
    try {
      setReport(await importFile(form).unwrap());
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось загрузить реестр поставщиков.');
    }
  }

  return (
    <Page>
      {can_write && (
        <Card>
          <SectionTitle>Загрузить реестр поставщиков</SectionTitle>
          <Hint>
            Таблица Excel или PDF: наименование, ИНН, КПП, контактное лицо, телефон, почта, что
            поставляет. Поставщики, которые уже есть по КП, дополнятся — их найдём по ИНН или
            наименованию; новые добавятся. Пустая ячейка в файле прежних данных не стирает.
          </Hint>
          <FileDropZone
            compact
            accept={ACCEPT_ANY}
            disabled={importing}
            primary={importing ? 'Загружаем реестр…' : undefined}
            hint="Перетащите файл или нажмите, чтобы выбрать."
            onFile={handleFile}
          />
          {report && (
            <Result role="status">
              Строк в файле: {report.rows}. Добавлено поставщиков: {report.created}, дополнено:{' '}
              {report.updated}
              {report.skipped > 0 ? `, пропущено строк без наименования: ${report.skipped}` : ''}.
            </Result>
          )}
          {error && <ErrBox role="alert">{error}</ErrBox>}
        </Card>
      )}

      <Card>
        <SectionTitle>
          Поставщики <Badge $tone="neutral">{items.length}</Badge>
          {items.length > 0 && <Badge $tone="neutral">с ИНН: {withInn}</Badge>}
        </SectionTitle>
        {isLoading ? (
          <Muted>Загружаем реестр…</Muted>
        ) : isError ? (
          <Muted>Не удалось загрузить реестр поставщиков.</Muted>
        ) : items.length === 0 ? (
          <Muted>
            Реестр пуст. Поставщики появятся после загрузки КП или реестра поставщиков файлом.
          </Muted>
        ) : (
          <>
            <Toolbar>
              <SearchWrap>
                <IconSearch size={15} />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Поиск: наименование, ИНН, сваи…"
                  aria-label="Поиск по реестру поставщиков"
                />
              </SearchWrap>
              <Muted role="status">
                показано {rows.length} из {items.length}
              </Muted>
            </Toolbar>
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th scope="col">Поставщик</th>
                    <th scope="col">ИНН</th>
                    <th scope="col">Контакты</th>
                    <th scope="col">Что поставляет</th>
                    <th scope="col" className="num">
                      КП
                    </th>
                    {can_write && <th scope="col" aria-label="Действия" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => {
                    const d = s.raw_data ?? {};
                    const details = detailsLine(s);
                    return editId === s.id ? (
                      <tr key={s.id}>
                        <DetailCell colSpan={can_write ? 6 : 5}>
                          <EditRow supplier={s} onDone={() => setEditId(null)} onError={setError} />
                          {error && <ErrBox role="alert">{error}</ErrBox>}
                        </DetailCell>
                      </tr>
                    ) : (
                      <tr key={s.id}>
                        <td>
                          <Name>{s.name}</Name>
                          {details && <Small>{details}</Small>}
                        </td>
                        <td>{s.inn ?? (d.inn_raw ? <Small>не ИНН: {d.inn_raw}</Small> : '—')}</td>
                        <td>{s.contact ?? '—'}</td>
                        <td>{d.specialization || '—'}</td>
                        <td className="num">
                          {s.offers}
                          {s.last_offer_at && <Small>от {fmtDate(s.last_offer_at)}</Small>}
                        </td>
                        {can_write && (
                          <td>
                            <Button
                              type="button"
                              $variant="ghost"
                              $size="sm"
                              onClick={() => {
                                setError('');
                                setEditId(s.id);
                              }}
                              aria-label={`Изменить поставщика ${s.name}`}
                            >
                              Изменить
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          </>
        )}
      </Card>
    </Page>
  );
}
